import { useCallback } from "react";
import { useDateFormatter } from "./date";
import { capitalizeFirstLetter } from "../utils";
import { MissionPayrollBreakdownFieldsFragment } from "../__generated__/operations/graphql";
import { useI18n } from "./i18n-hooks";

type PayrollBreakdownFormatterResult = {
    formatMonthYear: (pb: MissionPayrollBreakdownFieldsFragment) => string;
    formatPayrollDuration: (pb: MissionPayrollBreakdownFieldsFragment) => string;
    payrollBreakdownTitle: (pb: MissionPayrollBreakdownFieldsFragment) => string;
    screenLabel: (pb?: MissionPayrollBreakdownFieldsFragment) => string;
    screenSubLabel: (pb?: MissionPayrollBreakdownFieldsFragment) => string;
};

export const usePayrollBreakdownFormatter = (): PayrollBreakdownFormatterResult => {
    const intlFormat = useDateFormatter();
    const i18n = useI18n();

    const formatMonthYear = useCallback(
        (pb: MissionPayrollBreakdownFieldsFragment) => {
            return capitalizeFirstLetter(
                intlFormat(new Date(pb.yearMonth), {
                    year: "numeric",
                    month: "long",
                }),
            );
        },
        [intlFormat],
    );

    const payrollBreakdownTitle = useCallback(
        (pb: MissionPayrollBreakdownFieldsFragment) => {
            if (pb.isLast) {
                return i18n.t("screens.payroll_breakdown.final_settlement");
            }
            return formatMonthYear(pb);
        },
        [i18n, formatMonthYear],
    );
    const formatPayrollDuration = useCallback(
        (pb: MissionPayrollBreakdownFieldsFragment) => {
            if (pb.startDate > pb.endDate) {
                // this range occurs when a monthly mission start after the 20 of the first month.
                return "--";
            }
            const format = { day: "2-digit", month: "2-digit" } as const;
            const start = intlFormat(pb.startDate, format);
            const end = intlFormat(pb.endDate, format);
            return i18n.t("utils.period", { fromDate: start, toDate: end });
        },
        [i18n, intlFormat],
    );

    const screenLabel = useCallback(
        (pb?: MissionPayrollBreakdownFieldsFragment) => {
            if (pb?.isLast) {
                return i18n.t("screens.payroll_breakdown.final_settlement");
            }
            return i18n.t("payroll_breakdown.nav_title");
        },
        [i18n],
    );

    const screenSubLabel = useCallback(
        (pb?: MissionPayrollBreakdownFieldsFragment) => {
            if (!pb || pb.isLast) {
                return "";
            }
            return payrollBreakdownTitle(pb);
        },
        [payrollBreakdownTitle],
    );

    return {
        formatPayrollDuration,
        formatMonthYear,
        payrollBreakdownTitle,
        screenLabel,
        screenSubLabel,
    };
};
